import React, { useEffect, useState } from "react";
import Editor from "../editor";
import { useAppServices } from "../../../hook/services";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Article = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const AppService = useAppServices();
  const [articleName, setArticleName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [documentHtml, setDocumentHtml] = useState("");
  const [processing, setProcessing] = useState(true);
  const [saving, setSaving] = useState(false);

  const getData = async () => {
    const { response } = await AppService.knowledgebase_article.filter({
      query: `_id=${articleId}`,
    });
    if (response) {
      const data = response.data;
      setArticleName(data?.name);
      setShortDesc(data?.shortDescription);
      setDocumentHtml(data?.doc);
    } else {
      setArticleName("");
      setShortDesc("");
      setDocumentHtml("");
    }
    setProcessing(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        _id: articleId,
        name: articleName,
        shortDescription: shortDesc,
        doc: documentHtml,
      };

      const { response } = await AppService.knowledgebase_article.update({
        payload,
      });
      if (response) {
        toast.success("Article updated successfully");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (processing) {
    return (
      <div className="ka-page">
        <div className="container py-4" style={{ maxWidth: 880 }}>
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div className="ka-loader-icon d-inline-flex align-items-center justify-content-center mb-3">
              <div className="spinner-border text-primary" role="status" style={{ width: "1.6rem", height: "1.6rem", borderWidth: "2px" }} />
            </div>
            <div className="ka-loader-title">Loading article…</div>
            <div className="ka-loader-meta">Fetching the latest content</div>
          </div>

          {/* Skeleton form below */}
          <div className="ka-card mt-3">
            <div className="d-flex align-items-center gap-3 mb-4">
              <div className="ka-skel rounded" style={{ width: 40, height: 40 }} />
              <div className="flex-grow-1">
                <div className="ka-skel rounded" style={{ height: 12, width: "40%" }} />
                <div className="ka-skel rounded mt-2" style={{ height: 10, width: "60%" }} />
              </div>
            </div>
            <div className="ka-skel rounded mb-3" style={{ height: 38, width: "100%" }} />
            <div className="ka-skel rounded mb-3" style={{ height: 80, width: "100%" }} />
            <div className="ka-skel rounded" style={{ height: 220, width: "100%" }} />
          </div>
        </div>

        <KaStyles />
      </div>
    );
  }

  return (
    <div className="ka-page">
      <div className="container py-4" style={{ maxWidth: 880 }}>
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="ka-back-btn d-inline-flex align-items-center mb-3 bg-transparent border-0 p-0"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
        </button>

        {/* Header */}
        <header className="mb-4 d-flex align-items-center gap-3 flex-wrap">
          <span className="ka-header-icon d-inline-flex align-items-center justify-content-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
          </span>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h3 className="ka-title mb-1">Edit article</h3>
            <p className="ka-subtitle mb-0">
              Update the article name, description and content. Changes are saved when you press Update.
            </p>
          </div>
        </header>

        {/* Form */}
        <div className="ka-card">
          <form onSubmit={handleSubmit}>
            <div className="ka-field">
              <label className="ka-label">Article name</label>
              <input
                type="text"
                className="ka-input"
                placeholder="Enter article name"
                value={articleName}
                onChange={(e) => setArticleName(e.target.value)}
                required
              />
            </div>

            <div className="ka-field">
              <label className="ka-label">Short description</label>
              <textarea
                className="ka-input ka-textarea"
                rows="3"
                placeholder="Enter a short description"
                value={shortDesc}
                onChange={(e) => setShortDesc(e.target.value)}
                required
              />
            </div>

            <div className="ka-field">
              <label className="ka-label">Article content</label>
              <div className="ka-editor-wrap">
                <Editor
                  handleChange={(data) => setDocumentHtml(data)}
                  data={documentHtml}
                />
              </div>
            </div>

            <div className="ka-actions">
              <button
                type="submit"
                className="ka-btn ka-btn--primary w-100 d-inline-flex align-items-center justify-content-center"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Updating…
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Update article
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <KaStyles />
    </div>
  );
};

const KaStyles = () => (
  <style>{`
    .ka-page {
      font-family: 'Inter', sans-serif;
      background: #fafafa;
      min-height: 100vh;
    }

    .ka-back-btn {
      color: #6b7280;
      transition: color 0.15s ease;
    }
    .ka-back-btn:hover { color: #1f2937 !important; }
    .ka-back-btn:hover svg { transform: translateX(-2px); }
    .ka-back-btn svg { transition: transform 0.15s ease; }

    .ka-header-icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
      color: #4f46e5;
      flex-shrink: 0;
      border: 1px solid #e0e7ff;
      box-shadow: 0 1px 2px rgba(79, 70, 229, 0.06);
    }
    .ka-title {
      font-size: 1.375rem;
      font-weight: 700;
      letter-spacing: -0.015em;
      line-height: 1.15;
      color: #0f172a;
    }
    .ka-subtitle {
      font-size: 0.875rem;
      color: #6b7280;
      line-height: 1.55;
    }

    .ka-card {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 14px;
      padding: 22px 24px;
    }

    .ka-field { margin-bottom: 18px; }
    .ka-field:last-of-type { margin-bottom: 22px; }
    .ka-label {
      display: block;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 6px;
    }
    .ka-input {
      width: 100%;
      height: 40px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 0 12px;
      background: #fff;
      font-size: 0.875rem;
      color: #1f2937;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      font-family: inherit;
    }
    .ka-input::placeholder { color: #9ca3af; }
    .ka-input:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .ka-textarea {
      height: auto;
      padding: 10px 12px;
      line-height: 1.5;
      resize: vertical;
      min-height: 84px;
    }
    .ka-editor-wrap {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .ka-editor-wrap:focus-within {
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }

    .ka-actions { padding-top: 6px; }

    .ka-btn {
      font-size: 0.875rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 10px 18px;
      border-radius: 9px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .ka-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
    .ka-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
    }
    .ka-btn--primary {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22),
        0 4px 10px rgba(79, 70, 229, 0.2);
    }
    .ka-btn--primary:hover:not(:disabled) {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      transform: translateY(-1px);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(67, 56, 202, 0.28),
        0 8px 18px rgba(79, 70, 229, 0.3);
    }
    .ka-btn--primary:active:not(:disabled) { transform: translateY(0); }

    /* Loader */
    .ka-loader-icon {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
      border: 1px solid #e0e7ff;
      box-shadow: 0 4px 14px rgba(79, 70, 229, 0.12);
    }
    .ka-loader-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #0f172a;
      letter-spacing: -0.005em;
    }
    .ka-loader-meta {
      font-size: 0.8125rem;
      color: #6b7280;
      margin-top: 4px;
    }

    /* Skeleton */
    .ka-skel {
      background: linear-gradient(90deg, #eef2f7 0%, #f3f4f6 50%, #eef2f7 100%);
      background-size: 200% 100%;
      animation: ka-shimmer 1.4s ease-in-out infinite;
    }
    @keyframes ka-shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
  `}</style>
);

export default Article;
